import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Edit, Save, X, Bot, Play, Loader2, ExternalLink, CheckCircle2, AlertCircle, Clock, Globe } from 'lucide-react';
import { API } from '../../lib/apiBase';

const empty = {
  url: '',
  label: '',
  category: 'general',
  brand: '',
  model: '',
  frequency_hours: 24,
  enabled: true,
  max_depth: 0,
  max_pages: 10,
};

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'Genel' },
  { value: 'price_0km', label: '0km Fiyat' },
  { value: 'problem_review', label: 'Sorun / Yorum' },
];

const STATUS_BADGE = {
  success: { icon: CheckCircle2, cls: 'text-green-700 bg-green-50 border-green-200', label: 'Başarılı' },
  error:   { icon: AlertCircle, cls: 'text-destructive bg-destructive/10 border-destructive/30', label: 'Hata' },
  never:   { icon: Clock,       cls: 'text-muted-foreground bg-secondary border-border',         label: 'Henüz çalışmadı' },
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

const CrawlSourcesManager = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [runningId, setRunningId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/crawl-sources`, { withCredentials: true });
      setSources(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reset = () => { setForm(empty); setEditingId(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        frequency_hours: Number(form.frequency_hours) || 0,
        max_depth: Number(form.max_depth) || 0,
        max_pages: Number(form.max_pages) || 1,
      };
      if (editingId) {
        await axios.put(`${API}/admin/crawl-sources/${editingId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/crawl-sources`, payload, { withCredentials: true });
      }
      reset();
      load();
    } catch (err) {
      alert(`Hata: ${err.response?.data?.detail || 'Bir hata oluştu'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (s) => {
    setEditingId(s.id);
    setForm({
      url: s.url,
      label: s.label || '',
      category: s.category || 'general',
      brand: s.brand || '',
      model: s.model || '',
      frequency_hours: s.frequency_hours ?? 24,
      enabled: !!s.enabled,
      max_depth: s.max_depth ?? 0,
      max_pages: s.max_pages ?? 10,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kaynağı silmek istediğinizden emin misiniz?')) return;
    await axios.delete(`${API}/admin/crawl-sources/${id}`, { withCredentials: true });
    if (editingId === id) reset();
    load();
  };

  const handleRun = async (id) => {
    setRunningId(id);
    try {
      await axios.post(`${API}/admin/crawl-sources/${id}/run`, {}, { withCredentials: true });
      await load();
    } catch (err) {
      alert(`Hata: ${err.response?.data?.detail || 'Crawl başarısız'}`);
    } finally {
      setRunningId(null);
    }
  };

  return (
    <div data-testid="crawl-sources-manager">
      <div className="border border-border bg-secondary/30 p-4 mb-6 flex items-start gap-3">
        <Bot className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold mb-1">Crawl Kaynakları Nasıl Çalışır?</p>
          <p className="text-muted-foreground leading-relaxed">
            Buraya eklediğiniz URL'ler belirlediğiniz aralıkla otomatik çekilip <strong>Bilgi Bankası</strong>'na işlenir.
            AI, ilgili soru geldiğinde bu içerikleri referans alır. <strong>"Şimdi Çalıştır"</strong> ile manuel tetikleyebilirsiniz.
            Zamanlama <strong>0 saat</strong> seçilirse yalnızca manuel çalışır.
            <br/>İçerikler artık <strong>markdown formatında</strong> kaydedilir (başlıklar, listeler, tablolar korunur).
            <strong> Derinlik 1+</strong> seçilirse sayfadaki <em>aynı domain alt linkleri</em> de otomatik gezilir.
            <br/><span className="text-xs">⚠️ Anti-bot korumalı siteler (sahibinden vb.) ücretsiz crawl edilemez.</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Form */}
        <div className="border border-border bg-card p-6" data-testid="crawl-form-section">
          <h2 className="text-xl font-heading font-bold mb-6">
            {editingId ? 'Kaynağı Düzenle' : 'Yeni Crawl Kaynağı'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="crawl-url">URL *</Label>
              <Input id="crawl-url" type="url" required value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                className="rounded-sm font-mono text-sm" placeholder="https://..."
                data-testid="crawl-url-input" />
            </div>

            <div>
              <Label htmlFor="crawl-label">Etiket (opsiyonel)</Label>
              <Input id="crawl-label" value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                className="rounded-sm"
                placeholder="Örn: Arabam.com Toyota Corolla fiyatları"
                data-testid="crawl-label-input" />
            </div>

            <div>
              <Label>Kategori *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-sm" data-testid="crawl-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="crawl-brand">Marka (opsiyonel)</Label>
                <Input id="crawl-brand" value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value })}
                  className="rounded-sm" placeholder="BMW"
                  data-testid="crawl-brand-input" />
              </div>
              <div>
                <Label htmlFor="crawl-model">Model (opsiyonel)</Label>
                <Input id="crawl-model" value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                  className="rounded-sm" placeholder="3 Serisi"
                  data-testid="crawl-model-input" />
              </div>
            </div>

            <div>
              <Label htmlFor="crawl-freq">Zamanlama (saat) — 0 = sadece manuel</Label>
              <Input id="crawl-freq" type="number" min={0} max={720} value={form.frequency_hours}
                onChange={(e) => setForm({ ...form, frequency_hours: e.target.value })}
                className="rounded-sm w-32"
                data-testid="crawl-freq-input" />
            </div>

            <div className="grid grid-cols-2 gap-4 border-t border-border pt-4">
              <div>
                <Label htmlFor="crawl-depth">Derinlik (0-2) — 0 = sadece bu sayfa</Label>
                <Input id="crawl-depth" type="number" min={0} max={2} value={form.max_depth}
                  onChange={(e) => setForm({ ...form, max_depth: e.target.value })}
                  className="rounded-sm"
                  data-testid="crawl-depth-input" />
                <p className="text-xs text-muted-foreground mt-1">
                  1 = alt linkleri de gez, 2 = onların altını da
                </p>
              </div>
              <div>
                <Label htmlFor="crawl-max-pages">Maks. sayfa (1-50)</Label>
                <Input id="crawl-max-pages" type="number" min={1} max={50} value={form.max_pages}
                  onChange={(e) => setForm({ ...form, max_pages: e.target.value })}
                  className="rounded-sm"
                  data-testid="crawl-max-pages-input" />
                <p className="text-xs text-muted-foreground mt-1">
                  Bir crawl'da en fazla kaç sayfa
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={form.enabled}
                onCheckedChange={(v) => setForm({ ...form, enabled: v })}
                data-testid="crawl-enabled-switch" />
              <Label className="cursor-pointer">Etkin</Label>
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={submitting} className="rounded-sm bg-primary"
                data-testid="crawl-submit-button">
                {editingId ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                {submitting ? 'Kaydediliyor...' : editingId ? 'Güncelle' : 'Kaynak Ekle'}
              </Button>
              {editingId && (
                <Button type="button" variant="outline" onClick={reset} className="rounded-sm"
                  data-testid="crawl-cancel-button">
                  <X className="w-4 h-4 mr-2" />İptal
                </Button>
              )}
            </div>
          </form>
        </div>

        {/* List */}
        <div className="border border-border bg-card p-6" data-testid="crawl-list-section">
          <h2 className="text-xl font-heading font-bold mb-4">
            Mevcut Kaynaklar ({sources.length})
          </h2>

          {loading ? (
            <p className="text-muted-foreground">Yükleniyor...</p>
          ) : sources.length === 0 ? (
            <p className="text-muted-foreground">Henüz crawl kaynağı yok.</p>
          ) : (
            <div className="space-y-3 max-h-[700px] overflow-y-auto">
              {sources.map((s) => {
                const status = STATUS_BADGE[s.last_status || 'never'];
                const StatusIcon = status.icon;
                const running = runningId === s.id;
                return (
                  <div key={s.id} className="border border-border p-4" data-testid={`crawl-item-${s.id}`}>
                    <div className="flex justify-between items-start gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Globe className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                          <h3 className="font-semibold truncate">
                            {s.label || s.url}
                          </h3>
                          {!s.enabled && (
                            <span className="text-xs font-mono px-1.5 py-0.5 border border-border text-muted-foreground">
                              KAPALI
                            </span>
                          )}
                        </div>
                        <a href={s.url} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline font-mono flex items-center gap-1 truncate"
                          data-testid={`crawl-url-${s.id}`}>
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{s.url}</span>
                        </a>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button size="sm" variant="default" onClick={() => handleRun(s.id)} disabled={running}
                          className="rounded-sm h-8" data-testid={`crawl-run-${s.id}`} title="Şimdi Çalıştır">
                          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleEdit(s)}
                          className="rounded-sm h-8 w-8 p-0" data-testid={`crawl-edit-${s.id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(s.id)}
                          className="rounded-sm h-8 w-8 p-0" data-testid={`crawl-delete-${s.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 text-xs font-mono mt-2">
                      <span className={`px-1.5 py-0.5 border inline-flex items-center gap-1 ${status.cls}`}
                        data-testid={`crawl-status-${s.id}`}>
                        <StatusIcon className="w-3 h-3" />
                        {status.label}
                      </span>
                      <span className="px-1.5 py-0.5 border border-border bg-secondary text-foreground">
                        {CATEGORY_OPTIONS.find((c) => c.value === s.category)?.label || s.category}
                      </span>
                      {(s.brand || s.model) && (
                        <span className="px-1.5 py-0.5 border border-border bg-secondary text-foreground">
                          {s.brand} {s.model}
                        </span>
                      )}
                      <span className="px-1.5 py-0.5 border border-border bg-secondary text-foreground">
                        {s.frequency_hours > 0 ? `${s.frequency_hours} sa.` : 'Manuel'}
                      </span>
                      <span className="px-1.5 py-0.5 border border-border bg-secondary text-foreground" title="Derinlik / Maks. sayfa">
                        D{s.max_depth ?? 0} · {s.max_pages ?? 10} sayfa
                      </span>
                      {s.last_pages_crawled > 0 && (
                        <span className="px-1.5 py-0.5 border border-primary/30 bg-primary/10 text-primary"
                          title="Son crawl'da işlenen sayfa sayısı">
                          Son: {s.last_pages_crawled} sayfa
                        </span>
                      )}
                      <span className="text-muted-foreground self-center">
                        {formatDate(s.last_run_at)}
                      </span>
                    </div>

                    {s.last_error && (
                      <p className="mt-2 text-xs text-destructive font-mono truncate"
                        data-testid={`crawl-error-${s.id}`} title={s.last_error}>
                        {s.last_error}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrawlSourcesManager;
